import { Affix, Button, Flex, Text, TextInput, Title } from "@mantine/core";
import { usePOAStepsStore } from "../../../hooks/ui/poaStepsDataStore";
import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { z } from "zod";
import { useUpdateCustomerPOAData } from "../../../hooks/ui/customerPOADataStore";

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const phoneNumberRegex = /^(\+46|0)\d{7,9}$/;

const contactDetailsSchema = z.object({
  email: z.string().regex(emailRegex, "Must be a valid email address"),
  phoneNumber: z
    .string()
    .regex(phoneNumberRegex, "Must be a valid Swedish phone number"),
});

type ContactDetails = z.infer<typeof contactDetailsSchema>;

export default function AddContactDetails() {
  const { nextStep } = usePOAStepsStore();
  const { updateEmail, updatePhoneNumber, customerPOAData } =
    useUpdateCustomerPOAData();

  const { Field, handleSubmit, Subscribe } = useForm({
    defaultValues: {
      email: customerPOAData.email ?? "",
      phoneNumber: customerPOAData.phoneNumber ?? "",
    } as ContactDetails,
    onSubmit: async ({ value }) => {
      updateEmail(value.email);
      updatePhoneNumber(value.phoneNumber);
      nextStep();
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: contactDetailsSchema,
    },
  });

  return (
    <Flex justify="center" align="center" p="xl">
      <Flex direction="column" maw={672} w={"100%"}>
        <Title
          order={2}
          mb="1rem"
          ff={"var(--wallet-font-family-subjecttivity)"}
        >
          Hur kan vi kontakta dig?
        </Title>
        <Text mb={"xl"}>
          Vi använder email för att skicka dig information om din pension och
          ringer upp dig när det är dags för rådgivning. Uppgifterna kommer
          aldrig att delas med någon annan och används inte i
          marknadsföringssyfte.
        </Text>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }}
        >
          <Field
            name="email"
            children={({ state, handleChange, handleBlur }) => {
              return (
                <TextInput
                  placeholder="mail@mail.com"
                  label="Email"
                  mb={"lg"}
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(e) => handleChange(e.target.value)}
                  error={state.meta.errors}
                  type="email"
                />
              );
            }}
          />
          <Field
            name="phoneNumber"
            children={({ state, handleChange, handleBlur }) => {
              return (
                <TextInput
                  placeholder="07XXXXXXXX"
                  label="Telefon"
                  mb={"lg"}
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(e) => handleChange(e.target.value)}
                  type={"tel"}
                  error={state.meta.errors}
                />
              );
            }}
          />

          <Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Affix position={{ bottom: 0 }} bg={"white"} w={"100vw"} p={"lg"}>
                <Flex justify={"center"} gap={"md"}>
                  <Button
                    type="submit"
                    disabled={!canSubmit}
                    loading={isSubmitting}
                    onClick={() => handleSubmit()}
                  >
                    Nästa
                  </Button>
                </Flex>
              </Affix>
            )}
          />
        </form>
      </Flex>
    </Flex>
  );
}
